import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';

import { PtAuthSessionService } from '@pt/services/auth';

/** Fallback page shown when a signed-in user has no explicit redirect target. */
const DEFAULT_SIGNED_IN_REDIRECT = '/profile';

/** Shared route-protection check for authenticated branches. */
async function requireAuthenticatedSession(targetUrl: string): Promise<boolean | UrlTree> {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const authSession = inject(PtAuthSessionService);
  const router = inject(Router);

  const authenticated = await authSession.initializeSession();
  if (authenticated) {
    return true;
  }

  return router.createUrlTree(['/sign-in'], {
    queryParams: { redirectURL: targetUrl || DEFAULT_SIGNED_IN_REDIRECT },
  });
}

/**
 * Protects routes that require an authenticated cookie session.
 */
export const authGuard: CanActivateFn = (_route, state) => {
  return requireAuthenticatedSession(state.url);
};

/**
 * Mirrors authenticated checks for nested route activation.
 */
export const authChildGuard: CanActivateChildFn = (_route, state) => {
  return requireAuthenticatedSession(state.url);
};
